import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { authAPI, notificationAPI, taskAPI } from '../api';
import toast from 'react-hot-toast';
import { prefetchDashboard, invalidateDashboardCache } from '../utils/dashboardCache';
import { invalidateTaskListCache } from '../utils/taskListCache';
import { clearAllLookupCache } from '../utils/lookupCache';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } });
  const [theme,   setTheme]   = useState(() => localStorage.getItem('theme') || 'light');
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Apply theme to <html>
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

  const setAuth = (tokenData, userData) => {
    const token = typeof tokenData === 'object' ? tokenData.accessToken || tokenData.token : tokenData;
    const refreshToken = typeof tokenData === 'object' ? tokenData.refreshToken : null;

    localStorage.setItem('token', token);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      prefetchDashboard(taskAPI, userData);
    }
  };

  const logout = useCallback(() => {
    invalidateDashboardCache();
    invalidateTaskListCache();
    clearAllLookupCache();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    const rzpScript = document.querySelector('script[src*="razorpay"]');
        if (rzpScript) rzpScript.remove();
        if (window.Razorpay) {
          try { delete window.Razorpay; } catch {}
        }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authAPI.getMe();
      const u = data.data.user;
      localStorage.setItem('user', JSON.stringify(u));
      setUser(u);
      prefetchDashboard(taskAPI, u);
    } catch (err) {
      if (err.response?.status === 401) { logout(); }
    }
  }, [logout]);

  const updateUser = useCallback((updatedData) => {
    setUser((prev) => {
      const merged = { ...prev, ...updatedData };
      try {
        localStorage.setItem('user', JSON.stringify(merged));
      } catch (_) {}
      return merged;
    });
  }, []);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      const cachedUser = (() => {
        try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
      })();
      if (cachedUser) {
        prefetchDashboard(taskAPI, cachedUser);
      }
      refreshUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [refreshUser]);

  const fetchUnreadCount = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const { data } = await notificationAPI.getAll({ limit: 1, unread: 'true' });
      setUnreadCount(data.data.unreadCount || 0);
    } catch {}
  }, []);

  // Ref so socket handlers always call latest version (avoid stale closure)
  const fetchUnreadCountRef = useRef(fetchUnreadCount);
  useEffect(() => { fetchUnreadCountRef.current = fetchUnreadCount; }, [fetchUnreadCount]);

  const socketRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Polling fallback every 15s (backup when socket is down)
      const interval = setInterval(() => fetchUnreadCountRef.current(), 15000);

      // Real-time socket connection (like Mobile App)
      const token = localStorage.getItem('token');
      if (token) {
        const socketUrl = process.env.REACT_APP_SOCKET_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : window.location.origin);
        const socket = io(socketUrl, {
          auth: { token },
          transports: ['polling', 'websocket'],
          reconnectionAttempts: 3,
        });
        socketRef.current = socket;

        socket.on('notification', (data) => {
          fetchUnreadCountRef.current();

          // Suppress live toast popup if the action was triggered by current user themselves
          if (data.triggeredBy && data.triggeredBy === user?._id) {
            return;
          }

          toast.custom((t) => (
            <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white dark:bg-slate-900 shadow-2xl rounded-2xl pointer-events-auto flex ring-1 ring-black/5 border border-slate-200 dark:border-slate-800 p-4 transition-all duration-300`}>
              <div className="flex-1 w-0">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="w-9 h-9 rounded-xl bg-brand-500/10 dark:bg-brand-500/20 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold">
                      <svg className="w-5 h-5 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wide flex items-center gap-1.5">
                      <span>{data.title || 'Notification'}</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-ping" />
                    </p>
                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                      {data.message}
                    </p>
                    {data.type === 'timesheet_reminder' && (
                      <button
                        onClick={() => {
                          toast.dismiss(t.id);
                          window.location.href = '/timesheet';
                        }}
                        className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold rounded-lg shadow-sm transition-all active:scale-95"
                      >
                        <span>Fill Timesheet Now</span>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex border-l border-slate-200 dark:border-slate-800 ml-3 pl-2 items-start">
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="rounded-lg p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 focus:outline-none"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ), { duration: 6000 });
        });

        socket.on('broadcast', (data) => {
          fetchUnreadCountRef.current();
          toast.custom((t) => (
            <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-amber-500 text-white shadow-2xl rounded-2xl pointer-events-auto flex p-4 transition-all duration-300`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider">📢 Announcement: {data.title}</p>
                <p className="mt-1 text-xs leading-relaxed">{data.message}</p>
              </div>
              <button onClick={() => toast.dismiss(t.id)} className="ml-3 text-white/80 hover:text-white">✕</button>
            </div>
          ), { duration: 6000 });
        });
      }

      return () => {
        clearInterval(interval);
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
      };
    } else {
      setUnreadCount(0);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    }
  }, [user, fetchUnreadCount]);

  const isAdmin          = user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin     = user?.role === 'superadmin';
  const isManager        = user?.role === 'manager' || Boolean(user?.isManager);
  const isManagerOrAdmin = isAdmin || isManager;

  const hasFeature = useCallback((featureName) => {
    if (user?.role === 'superadmin') return true;
    if (!user) return false;
    if (user?.plan?.features) {
      return Boolean(user.plan.features[featureName]);
    }
    // Safe default if plan features not yet loaded in context:
    if (featureName === 'attendanceTracking') return true;
    return false; // Default false for paid features (misReports, broadcastEmail, broadcastWhatsapp)
  }, [user]);

  const plan = user?.plan || null;

  return (
    <AuthContext.Provider value={{ user, setAuth, logout, refreshUser, updateUser, loading, isAdmin, isSuperAdmin, isManager, isManagerOrAdmin, theme, toggleTheme, unreadCount, setUnreadCount, fetchUnreadCount, hasFeature, plan }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
